import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './Reports.css';
import './PublisherDashboard.css';

interface PublisherDashboardProps {
  publisherUsername: string;
}

const PublisherDashboard: React.FC<PublisherDashboardProps> = ({ publisherUsername }) => {
  const [publisherData, setPublisherData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [todayOffersReport, setTodayOffersReport] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const isAdminView = localStorage.getItem('userType') === 'admin';
        const publisherId = isAdminView ? publisherUsername : localStorage.getItem('publisherId');
        if (!publisherId) {
          throw new Error('Publisher ID not found. Please login again.');
        }

        const publisherResponse = isAdminView
          ? await apiService.getPublisher(parseInt(publisherId, 10))
          : await apiService.getPublisherMe();
        const publisher = publisherResponse.publisher;

        if (publisher) {
          // Fetch real performance metrics from the API
          const metricsResponse = await apiService.getPublisherMetrics(publisher.id);
          const metrics = metricsResponse.metrics;

          // Use actual today earnings from API (no rounding error)
          const todayRevenue = parseFloat(metrics.todayEarnings || '0');

          setPublisherData({
            id: publisher.id,
            name: publisher.name || '',
            clicksToday: metrics.clicksToday,
            conversionsToday: metrics.conversionsToday,
            cvrToday: parseFloat(metrics.cvrToday),
            revenueToday: todayRevenue
          });

          // Fetch today's offer-wise report
          const todayReportResponse = await apiService.getPublisherTodayOffersReport(publisher.id);
          setTodayOffersReport(todayReportResponse.report || []);
        } else {
          setError('Publisher not found');
        }

      } catch (err: any) {
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [publisherUsername]);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (!publisherData) {
    return <div className="error">Publisher data not found</div>;
  }

  // Calculate totals for today's report
  const reportTotals = todayOffersReport.reduce((acc, row) => ({
    clicks: acc.clicks + row.clicks,
    conversions: acc.conversions + row.conversions,
    revenue: acc.revenue + parseFloat(row.revenue)
  }), { clicks: 0, conversions: 0, revenue: 0 });

  const totalCR = reportTotals.clicks > 0
    ? ((reportTotals.conversions / reportTotals.clicks) * 100).toFixed(2)
    : '0.00';

  return (
    <div className="publisher-dashboard-page">
        <div className="publisher-section-header">
          <div>
            <h1>Dashboard</h1>
            <p>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>

        <div className="report-summary publisher-summary" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <div className="summary-card">
            <h3>Today's Clicks</h3>
            <div className="value" style={{ color: '#3498db' }}>{publisherData.clicksToday.toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <h3>Conversions</h3>
            <div className="value approved">{publisherData.conversionsToday.toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <h3>CR%</h3>
            <div className="value" style={{ color: '#f39c12' }}>{publisherData.cvrToday.toFixed(2)}%</div>
          </div>
          <div className="summary-card">
            <h3>Revenue</h3>
            <div className="value" style={{ color: '#27ae60' }}>${publisherData.revenueToday.toFixed(2)}</div>
          </div>
        </div>

        {/* Today's Report Section */}
        <div className="publisher-report-section">
          <div className="publisher-section-header publisher-report-header">
            <div>
              <h2>Today's Report</h2>
              <p>Offer-level performance for the current day.</p>
            </div>
          </div>
          <div className="report-content publisher-report-content">
            <div className="table-container">
              <table className="report-table publisher-today-report-table">
                <thead>
                  <tr>
                    <th>Offer ID</th>
                    <th>Offer Name</th>
                    <th>Clicks</th>
                    <th>Conversions</th>
                    <th>CR%</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {todayOffersReport.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-cell">
                        No data available for today
                      </td>
                    </tr>
                  ) : (
                    todayOffersReport.map((row, index) => (
                      <tr key={index}>
                        <td>{row.offer_id}</td>
                        <td>
                          <div className="offer-cell">
                            <span className="offer-name">{row.offer_name}</span>
                          </div>
                        </td>
                        <td>{row.clicks.toLocaleString()}</td>
                        <td>
                          <span className="approved">{row.conversions}</span>
                        </td>
                        <td>
                          <span className="cvr">{row.cr}%</span>
                        </td>
                        <td>
                          <span className="revenue">${row.revenue}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {todayOffersReport.length > 0 && (
                  <tfoot>
                    <tr className="totals-row">
                      <td colSpan={2}><strong>Total</strong></td>
                      <td><strong>{reportTotals.clicks.toLocaleString()}</strong></td>
                      <td><strong>{reportTotals.conversions}</strong></td>
                      <td><strong>{totalCR}%</strong></td>
                      <td><strong>${reportTotals.revenue.toFixed(2)}</strong></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>
  );
};

export default PublisherDashboard;
